import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import FormInput from '../../components/shared/FormInput';
import { DEFAULT_RISK_PERCENT, POSITION_MODES, RISK_PERCENT_BASES, SIZE_MODES } from '../engine/positionMode';

export default function BacktestSettings({
  positionMode,
  onPositionMode,
  sizeMode,
  onSizeMode,
  fixedQty,
  onFixedQty,
  maxRisk,
  onMaxRisk,
  startingBalance,
  onStartingBalance,
  riskPercent,
  onRiskPercent,
  riskPercentBasis,
  onRiskPercentBasis,
}) {
  const { colors } = useTheme();
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div data-testid="backtest-settings" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={labelStyle(colors)}>Starting balance</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 180 }}>
          <span style={{ color: colors.textMuted, fontSize: 13, fontWeight: 650 }}>$</span>
          <FormInput
            type="number"
            min={1}
            step={1}
            value={startingBalance}
            onChange={(e) => {
              const n = Number(e.target.value);
              onStartingBalance?.(Number.isFinite(n) && n > 0 ? n : startingBalance);
            }}
            style={{ width: '100%', height: 32, fontVariantNumeric: 'tabular-nums' }}
          />
        </div>
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={labelStyle(colors)}>Quantity</span>
        <div
          role="radiogroup"
          aria-label="Size method"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
        >
          {SIZE_MODES.map((mode) => {
            const active = mode.id === sizeMode;
            const caution = isCautionSizeMode(mode.id, riskPercent);
            const accent = caution ? (colors.danger || '#F73263') : colors.primary;
            return (
              <button
                key={mode.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onSizeMode?.(mode.id)}
                style={{
                  flex: 1,
                  minHeight: 32,
                  padding: '6px 10px',
                  borderRadius: caution ? 999 : 8,
                  border: `1px solid ${active || caution ? accent : colors.border}`,
                  background: active
                    ? `${accent}22`
                    : (caution ? `${accent}14` : (colors.bgInput || 'transparent')),
                  color: active || caution ? accent : colors.text,
                  fontSize: 11,
                  fontWeight: 650,
                  cursor: 'pointer',
                  lineHeight: 1.25,
                }}
              >
                {mode.label}
              </button>
            );
          })}
        </div>
        {sizeMode === 'risk' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 180 }}>
              <span style={{ color: colors.textMuted, fontSize: 13, fontWeight: 650 }}>$</span>
              <FormInput
                type="number"
                min={1}
                step={1}
                value={maxRisk}
                aria-label="Max risk per trade"
                onChange={(e) => {
                  const n = Number(e.target.value);
                  onMaxRisk?.(Number.isFinite(n) && n > 0 ? n : maxRisk);
                }}
                style={{ width: '100%', height: 32, fontVariantNumeric: 'tabular-nums' }}
              />
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted, lineHeight: 1.35 }}>
              Contracts = max risk ÷ (stop distance × point value), rounded down. Skips the trade below 1.
            </div>
          </>
        ) : sizeMode === 'percent' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, maxWidth: 180 }}>
              <FormInput
                type="number"
                min={0.01}
                step={0.1}
                value={riskPercent}
                aria-label="Risk percent"
                onChange={(e) => {
                  const n = Number(e.target.value);
                  onRiskPercent?.(Number.isFinite(n) && n > 0 ? n : riskPercent);
                }}
                style={{ width: '100%', height: 32, fontVariantNumeric: 'tabular-nums' }}
              />
              <span style={{ color: colors.textMuted, fontSize: 13, fontWeight: 650 }}>%</span>
            </div>
            <div
              role="radiogroup"
              aria-label="Percent of account basis"
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              {RISK_PERCENT_BASES.map((basis) => {
                const active = basis.id === riskPercentBasis;
                return (
                  <button
                    key={basis.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => onRiskPercentBasis?.(basis.id)}
                    style={{
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: `1px solid ${active ? colors.primary : colors.border}`,
                      background: active ? `${colors.primary}18` : colors.bgInput || 'transparent',
                      color: colors.text,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 650 }}>{basis.label}</div>
                    <div style={{ fontSize: 11, color: colors.textMuted, lineHeight: 1.35, marginTop: 2 }}>
                      {basis.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <FormInput
              type="number"
              min={1}
              step={1}
              value={fixedQty}
              aria-label="Fixed quantity"
              onChange={(e) => {
                const n = Number(e.target.value);
                onFixedQty?.(Number.isFinite(n) && n >= 1 ? Math.floor(n) : fixedQty);
              }}
              style={{ maxWidth: 180, height: 32, fontVariantNumeric: 'tabular-nums' }}
            />
            <div style={{ fontSize: 11, color: colors.textMuted, lineHeight: 1.35 }}>
              Whole shares or contracts. A 1-lot uses one take profit.
            </div>
          </>
        )}
      </div>

      <div>
        <div
          style={{
            ...labelStyle(colors),
            marginBottom: 8,
            textTransform: 'none',
            letterSpacing: 0,
            lineHeight: 1.35,
          }}
        >
          When a new signal fires and position already exists
        </div>
        <div role="radiogroup" aria-label="Position mode" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {POSITION_MODES.map((mode) => {
            const active = mode.id === positionMode;
            const open = expanded.has(mode.id);
            return (
              <div
                key={mode.id}
                style={{
                  borderRadius: 8,
                  border: `1px solid ${active ? colors.primary : colors.border}`,
                  background: active ? `${colors.primary}18` : colors.bgInput || 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => onPositionMode?.(mode.id)}
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      padding: '8px 10px',
                      border: 'none',
                      background: 'transparent',
                      color: colors.text,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 650,
                    }}
                  >
                    {mode.label}
                  </button>
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={`${mode.label} details`}
                    onClick={() => toggleExpanded(mode.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      marginRight: 4,
                      border: 'none',
                      background: 'transparent',
                      color: colors.textMuted,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {open ? (
                  <div style={{ fontSize: 11, color: colors.textMuted, padding: '0 10px 8px', lineHeight: 1.35 }}>
                    {mode.description}
                    {mode.emphasis ? (
                      <>
                        {' '}
                        <strong style={{ color: colors.text, fontWeight: 700 }}>{mode.emphasis}</strong>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function isCautionSizeMode(modeId, riskPercent) {
  return modeId === 'percent' && Number(riskPercent) > DEFAULT_RISK_PERCENT;
}

function labelStyle(colors) {
  return {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: colors.textMuted,
  };
}
